import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  InputLabel,
  MenuItem,
  TextField,
} from "@mui/material";
import { Form, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { createPettyPurchase } from "../../../../Redux/Actions/PettyPurchases";
import { errorNotify, successNotify } from "../../../../Utils/toast";
import { clearErrors } from "../../../../Redux/Actions/Auth";
import Modal from "../../../../Components/Modal/Modal";
import { useStyle } from "../Attendance/attendance-styles";
import { BsFillFileImageFill } from "react-icons/bs";

const AddPettyPurchaseModal = ({ modalOpen, setModalOpen }) => {
  const classes = useStyle();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.currentUser);
  const { loading, error, success } = useSelector(
    (state) => state.createPettyPurchase
  );

  // Check if user has access to this module
  const hasAccess =
    (user?.user?.department?._id === "68875ce1f9c0eda4dcce9c74" ||
      user?.user?.department?._id === "6392e6b4758449db0ae81600" ||
      user?.user?.department?._id === "63d8c6158c662fd4d455ce85" ||
      user?.user?.department?._id === "6698ff124aa01ad1714d88b3") &&
    (user?.user?.role === "HR" ||
      user?.user?.role === "admin" ||
      user?.user?.role === "employee");

  const [formData, setFormData] = useState({
    item_name: "",
    quantity: "",
    product_code: "",
    item_last_price: "",
    last_purchase_date: "",
    estimated_unit_cost: "",
    estimated_total_cost: "",
    description: "",
    priority: "medium",
    required_by_date: "",
    vendor_details: "",
  });

  const [docs, setDocs] = useState([]);
  const [previewDocs, setPreviewDocs] = useState([]);
  const [extDocs, setExtDocs] = useState([]);

  useEffect(() => {
    if (success) {
      successNotify("Petty Pruchase Order created successfully!");
      setModalOpen(false);
      resetForm();
      // Do NOT dispatch reset here; let parent handle it
    }
    if (error) {
      errorNotify(error);
      dispatch(clearErrors());
    }
  }, [success, error, setModalOpen, dispatch]);

  const resetForm = () => {
    setFormData({
      item_name: "",
      quantity: "",
      product_code: "",
      item_last_price: "",
      last_purchase_date: "",
      estimated_unit_cost: "",
      estimated_total_cost: "",
      description: "",
      priority: "medium",
      required_by_date: "",
      vendor_details: "",
    });
    setDocs([]);
    setPreviewDocs([]);
    setExtDocs([]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const uploadDocsChange = (e) => {
    const files = Array.from(e.target.files);
    Promise.all(files.map((file) => URL.createObjectURL(file))).then((urls) => {
      setPreviewDocs(urls);
    });
    Promise.all(files.map((file) => file.name)).then((ext) => {
      setExtDocs(ext);
    });
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.readyState === 2) {
          setDocs((old) => [...old, reader.result]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!hasAccess) {
      errorNotify("You don't have permission to create petty purchase order");
      return;
    }

    if (!formData.item_name || !formData.quantity) {
      errorNotify("Please fill in all required fields");
      return;
    }

    const flattenedDocs = Array.isArray(docs)
      ? docs.flat
        ? docs.flat(Infinity)
        : docs.reduce((acc, v) => acc.concat(v), [])
      : [];

    const payload = {
      item_name: formData.item_name,
      quantity: formData.quantity,
      product_code: formData.product_code,
      item_last_price: formData.item_last_price,
      last_purchase_date: formData.last_purchase_date,
      estimated_unit_cost: formData.estimated_unit_cost,
      estimated_total_cost: formData.estimated_total_cost,
      description: formData.description,
      priority: formData.priority,
      required_by_date: formData.required_by_date,
      vendor_details: formData.vendor_details,
      petty_purchase_docs: flattenedDocs,
    };

    dispatch(createPettyPurchase(payload));
  };

  const handleClose = () => {
    setModalOpen(false);
    resetForm();
  };

  return (
    <Modal
      open={modalOpen}
      setOpen={setModalOpen}
      title="Petty Purchase Order Form"
      size="800"
    >
      <React.Fragment>
        <Form onSubmit={handleSubmit}>
          <Box>
            <Grid container columnSpacing={7}>
              <Grid item md={6}>
                <Box sx={{ my: 2 }}>
                  <InputLabel className={classes.department_label}>
                    Product Code
                  </InputLabel>
                  <TextField
                    sx={{ mt: 1 }}
                    placeholder="Enter Product Code"
                    fullWidth
                    className={classes.inputContainer}
                    name="product_code"
                    value={formData.product_code}
                    onChange={handleInputChange}
                  />
                </Box>
              </Grid>
              <Grid item md={6} />

              <Grid item md={6}>
                <Box sx={{ my: 2 }}>
                  <InputLabel className={classes.department_label}>
                    Item Name *
                  </InputLabel>
                  <TextField
                    sx={{ mt: 1 }}
                    placeholder="Enter Item Name"
                    fullWidth
                    required
                    className={classes.inputContainer}
                    name="item_name"
                    value={formData.item_name}
                    onChange={handleInputChange}
                  />
                </Box>
              </Grid>

              <Grid item md={6}>
                <Box sx={{ my: 2 }}>
                  <InputLabel className={classes.department_label}>
                    Quantity *
                  </InputLabel>
                  <TextField
                    sx={{ mt: 1 }}
                    placeholder="Enter Quantity"
                    type="number"
                    fullWidth
                    required
                    className={classes.inputContainer}
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleInputChange}
                  />
                </Box>
              </Grid>

              <Grid item md={6}>
                <Box sx={{ my: 2 }}>
                  <InputLabel className={classes.department_label}>
                    Last Purchased Date
                  </InputLabel>
                  <TextField
                    sx={{ mt: 1 }}
                    type="date"
                    fullWidth
                    className={classes.inputContainer}
                    name="last_purchase_date"
                    value={formData.last_purchase_date}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </Grid>

              <Grid item md={6}>
                <Box sx={{ my: 2 }}>
                  <InputLabel className={classes.department_label}>
                    Item Last Price
                  </InputLabel>
                  <TextField
                    sx={{ mt: 1 }}
                    placeholder="Enter the last price item was purchased at"
                    type="number"
                    fullWidth
                    className={classes.inputContainer}
                    name="item_last_price"
                    value={formData.item_last_price}
                    onChange={handleInputChange}
                  />
                </Box>
              </Grid>

              <Grid item md={6}>
                <Box sx={{ my: 2 }}>
                  <InputLabel className={classes.department_label}>
                    Estimated Unit Cost
                  </InputLabel>
                  <TextField
                    sx={{ mt: 1 }}
                    placeholder="Enter estimated cost of one unit"
                    type="number"
                    fullWidth
                    className={classes.inputContainer}
                    name="estimated_unit_cost"
                    value={formData.estimated_unit_cost}
                    onChange={handleInputChange}
                  />
                </Box>
              </Grid>

              <Grid item md={6}>
                <Box sx={{ my: 2 }}>
                  <InputLabel className={classes.department_label}>
                    Estimated Total Cost
                  </InputLabel>
                  <TextField
                    sx={{ mt: 1 }}
                    placeholder="Enter estimated total cost for all units"
                    type="number"
                    fullWidth
                    className={classes.inputContainer}
                    name="estimated_total_cost"
                    value={formData.estimated_total_cost}
                    onChange={handleInputChange}
                  />
                </Box>
              </Grid>

              <Grid item md={6}>
                <Box sx={{ my: 2 }}>
                  <InputLabel className={classes.department_label}>
                    Required By Date
                  </InputLabel>
                  <TextField
                    sx={{ mt: 1 }}
                    type="date"
                    fullWidth
                    className={classes.inputContainer}
                    name="required_by_date"
                    value={formData.required_by_date}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              </Grid>

              <Grid item md={6}>
                <Box sx={{ my: 2 }}>
                  <InputLabel className={classes.department_label}>
                    Priority
                  </InputLabel>
                  <TextField
                    sx={{ mt: 1 }}
                    select
                    fullWidth
                    className={classes.inputContainer}
                    name="priority"
                    value={formData.priority}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </TextField>
                </Box>
              </Grid>

              <Grid item md={12}>
                <Box sx={{ my: 2 }}>
                  <InputLabel className={classes.department_label}>
                    Vendor Details
                  </InputLabel>
                  <TextField
                    sx={{ mt: 1 }}
                    placeholder="Enter vendor details (if known)"
                    fullWidth
                    rows={1}
                    multiline
                    className={classes.inputContainer}
                    name="vendor_details"
                    value={formData.vendor_details}
                    onChange={handleInputChange}
                  />
                </Box>
              </Grid>

              <Grid item md={12}>
                <Box sx={{ my: 2 }}>
                  <InputLabel className={classes.department_label}>
                    Description *
                  </InputLabel>
                  <TextField
                    sx={{ mt: 1 }}
                    placeholder="Enter detailed description"
                    fullWidth
                    rows={3}
                    multiline
                    required
                    className={classes.inputContainer}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                  />
                </Box>
              </Grid>

              <Grid
                item
                md={12}
                sx={{ display: "flex", gap: "25px", mt: 2, flexWrap: "wrap" }}
              >
                {previewDocs.length !== 0 &&
                  previewDocs.map((d, i) => {
                    return (
                      <div
                        style={{
                          width: "90px",
                          height: "120px",
                          border: "1px solid lightgrey",
                          padding: "5px",
                        }}
                      >
                        {extDocs[i]?.includes(".pdf") && (
                          <iframe src={d} width="100%" height="100px" />
                        )}
                        {(extDocs[i]?.includes(".png") ||
                          extDocs[i]?.includes(".jpg") ||
                          extDocs[i]?.includes(".jpeg")) && (
                          <img
                            style={{
                              width: "100%",
                              objectFit: "cover",
                              height: "100px",
                            }}
                            src={d}
                          />
                        )}
                      </div>
                    );
                  })}
              </Grid>

              <Grid item md={12} sx={{ mt: 3 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "20px",
                  }}
                >
                  <Button variant="contained" component="label">
                    Upload Documents
                    <BsFillFileImageFill
                      style={{ fontSize: "26px", marginLeft: "10px" }}
                    />
                    <input
                      hidden
                      accept="image/*, .pdf"
                      multiple
                      type="file"
                      onChange={uploadDocsChange}
                    />
                  </Button>

                  <p style={{ marginBottom: "0px", color: "red" }}>
                    *Supported files: Images and pdf only.
                  </p>
                </div>
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "20px",
            }}
          >
            <Box sx={{ display: "flex", gap: "10px" }}>
              <Button
                variant="contained"
                type="button"
                className={classes.cancelBtn}
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={loading ? true : false}
                className={classes.createLeaveBtn}
              >
                {loading ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  "Create Order"
                )}
              </Button>
            </Box>
          </Box>
        </Form>
      </React.Fragment>
    </Modal>
  );
};

export default AddPettyPurchaseModal;
